/**
 * Hero Banner — Frontend JS
 *
 * Handles deferred video loading and autoplay with audio-first strategy.
 * If autoplay with audio is blocked by the browser, it falls back to muted autoplay.
 */
const HERO_BANNER_AUDIO_PREFERENCE_COOKIE = "wombat_hero_banner_audio";
const HERO_BANNER_LOGO_HIDE_DELAY_MS = 1000;

document.querySelectorAll( ".b-hero-banner" ).forEach( ( block ) => {

	const video = block.querySelector( ".b-hero-banner__bg-video" );

	if ( !video ) {

		return;

	}

	const videoSrc = video.dataset.src;

	if ( !videoSrc ) {

		return;

	}

	const audioToggle = block.querySelector( ".b-hero-banner__audio-toggle" );

	/**
	 * Read cookie value by name.
	 *
	 * @param {string} cookieName Cookie key.
	 * @return {string} Cookie value or empty string.
	 */
	const getCookieValue = ( cookieName ) => {

		const cookiePrefix = `${cookieName}=`;
		const cookies = document.cookie.split( "; " );
		const matchedCookie = cookies.find(
			( item ) => item.indexOf( cookiePrefix ) === 0,
		);

		if ( !matchedCookie ) {

			return "";

		}

		return matchedCookie.slice( cookiePrefix.length );

	};

	/**
	 * Get saved audio preference from cookie.
	 *
	 * @return {boolean|null} Muted state or null if not set.
	 */
	const getSavedMutedState = () => {

		const savedPreference = getCookieValue(
			HERO_BANNER_AUDIO_PREFERENCE_COOKIE,
		);

		if ( savedPreference === "muted" ) {

			return true;

		}

		if ( savedPreference === "unmuted" ) {

			return false;

		}

		return null;

	};

	/**
	 * Save audio preference in cookie.
	 *
	 * @param {boolean} isMuted Whether audio should stay muted.
	 */
	const saveMutedState = ( isMuted ) => {

		const cookieValue = isMuted ? "muted" : "unmuted";

		document.cookie = `${HERO_BANNER_AUDIO_PREFERENCE_COOKIE}=${cookieValue}; path=/; max-age=31536000; SameSite=Lax`;

	};

	let preferredMutedState = getSavedMutedState();

	/**
	 * Check whether the video element has an audio track.
	 *
	 * @return {boolean} True when audio is available.
	 */
	const hasAudioTrack = () => {

		if ( typeof video.mozHasAudio === "boolean" ) {

			return video.mozHasAudio;

		}

		if ( video.audioTracks && typeof video.audioTracks.length === "number" ) {

			return video.audioTracks.length > 0;

		}

		return true;

	};

	/**
	 * Sync button state with current muted status.
	 *
	 * @param {boolean} isMuted Whether the video is muted.
	 */
	const setAudioToggleState = ( isMuted ) => {

		if ( !audioToggle ) {

			return;

		}

		audioToggle.classList.toggle( "is-muted", isMuted );
		audioToggle.setAttribute( "aria-pressed", isMuted ? "true" : "false" );

		const mutedLabel = audioToggle.dataset.labelMuted || "Włącz dźwięk";
		const unmutedLabel = audioToggle.dataset.labelUnmuted || "Wycisz film";

		audioToggle.setAttribute(
			"aria-label",
			isMuted ? mutedLabel : unmutedLabel,
		);

	};

	/**
	 * Bind click handler for mute/unmute control.
	 */
	const bindAudioToggle = () => {

		if ( !audioToggle ) {

			return;

		}

		audioToggle.addEventListener( "click", () => {

			const nextMutedState = !video.muted;
			video.muted = nextMutedState;
			video.defaultMuted = nextMutedState;
			setAudioToggleState( nextMutedState );

			preferredMutedState = nextMutedState;
			saveMutedState( nextMutedState );

		} );

	};

	/**
	 * Try to autoplay with sound first, then fallback to muted autoplay.
	 *
	 * @return {Promise<void>} Promise resolved when playback starts.
	 */
	const playWithAutoplayFallback = () => {

		const shouldStartMuted = preferredMutedState === true;

		video.muted = shouldStartMuted;
		video.defaultMuted = shouldStartMuted;
		setAudioToggleState( shouldStartMuted );

		if ( shouldStartMuted ) {

			return video.play();

		}

		return video.play().catch( () => {

			video.muted = true;
			video.defaultMuted = true;
			setAudioToggleState( true );

			return video.play();

		} );

	};

	/**
	 * Load and play the background video after page load.
	 * Uses requestIdleCallback (with setTimeout fallback) to defer
	 * video loading until the browser is idle — keeps LCP fast.
	 */
	const loadVideo = () => {

		video.src = videoSrc;
		video.load();

		video.addEventListener(
			"loadedmetadata",
			() => {

				if ( audioToggle && hasAudioTrack() ) {

					audioToggle.hidden = false;

				}

			},
			{ once: true },
		);

		video.addEventListener(
			"canplay",
			() => {

				playWithAutoplayFallback()
					.then( () => {

						video.classList.add( "is-playing" );

						setTimeout( () => {

							block.classList.add( "is-video-playing" );

						}, HERO_BANNER_LOGO_HIDE_DELAY_MS );

					} )
					.catch( () => {
						// Autoplay blocked — keep showing the image fallback.
					} );

			},
			{ once: true },
		);

	};

	bindAudioToggle();

	if ( "requestIdleCallback" in window ) {

		window.requestIdleCallback( loadVideo, { timeout: 3000 } );

	} else {

		setTimeout( loadVideo, 2000 );

	}

} );
